import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, collection, query, where, orderBy, limit, getDocs, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { LedgerTransaction } from "../types";

// Get Firestore instance
const db = getFirestore();
const functions = getFunctions();

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  externalAccountId?: string;
  role: string;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  phone?: string;
  clubId?: string;
}

export interface QRCodeData {
  qrCodeId: string;
  qrImage: string;
  encodedData: string;
  payload: {
    userId: string;
    clubId: string;
    timestamp: number;
    type: string;
    version: number;
  };
  createdAt: Date;
}

/**
 * Get user profile information
 */
export async function getUserProfile(userId: string): Promise<UserProfile> {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const userDoc = await getDoc(doc(db, "users", userId));

    if (!userDoc.exists()) {
      // If document doesn't exist, create it from Auth user data
      if (currentUser && currentUser.uid === userId) {
        const newProfile: UserProfile = {
          userId,
          email: currentUser.email || "",
          displayName: currentUser.displayName || "",
          role: "END_USER",
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        // Don't await this - it's a background operation
        updateDoc(doc(db, "users", userId), {
          userId,
          email: newProfile.email,
          displayName: newProfile.displayName,
          role: newProfile.role,
          updatedAt: new Date(),
        }).catch((err) => console.error("Error creating user profile:", err));
        return newProfile;
      }
      throw new Error("User not found");
    }

    const data = userDoc.data();
    return {
      userId,
      email: data.email || "",
      displayName: data.displayName || "",
      externalAccountId: data.externalAccountId,
      role: data.role || "END_USER",
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
      lastLoginAt: data.lastLoginAt?.toDate?.(),
      phone: data.phone,
      clubId: data.clubId,
    };
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<UserProfile>
): Promise<void> {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser || currentUser.uid !== userId) {
      throw new Error("Unauthorized: Can only update your own profile");
    }

    const userRef = doc(db, "users", userId);

    const updateData: any = {
      updatedAt: new Date(),
    };

    if (updates.displayName) updateData.displayName = updates.displayName;
    if (updates.phone) updateData.phone = updates.phone;
    if (updates.externalAccountId) updateData.externalAccountId = updates.externalAccountId;

    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
}

/**
 * Generate QR code for user account
 */
export async function generateQRCode(userId: string): Promise<QRCodeData> {
  try {
    const generateQRCodeFn = httpsCallable(functions, "generate_qr_code");
    const result = await generateQRCodeFn({ userId });

    const data = result.data as any;
    if (!data.success) {
      throw new Error("Failed to generate QR code");
    }

    return {
      qrCodeId: data.qrCodeId,
      qrImage: data.qrImage,
      encodedData: data.encodedData,
      payload: data.payload,
      createdAt: new Date(data.createdAt),
    };
  } catch (error) {
    console.error("Error generating QR code:", error);
    throw error;
  }
}

/**
 * Get user activity log (recent transactions)
 */
export async function getUserActivityLog(
  userId: string,
  limitCount: number = 20
): Promise<LedgerTransaction[]> {
  try {
    const txQuery = query(
      collection(db, "ledger_transactions"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );

    const snapshot = await getDocs(txQuery);
    const transactions: LedgerTransaction[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        txId: doc.id,
        userId: data.userId,
        type: data.type,
        amount: data.amount,
        reason: data.reason,
        status: data.status,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        walletItemId: data.walletItemId,
        createdBy: data.actedBy,
      } as LedgerTransaction);
    });

    return transactions;
  } catch (error) {
    console.error("Error fetching activity log:", error);
    return [];
  }
}

/**
 * Format date for display
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("no-NO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * Format transaction type for display
 */
export function formatTransactionType(type: string): string {
  const types: { [key: string]: string } = {
    ISSUE: "🎁 Issued",
    CLAIM: "📥 Claimed",
    REDEEM: "💳 Redeemed",
    QR_PAYMENT: "📱 QR Payment",
    MANUAL_DEBIT: "✏️ Manual Debit",
    EXPIRY: "⏰ Expired",
  };
  return types[type] || type;
}
